import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import styles from './DataLakeSettingsTab.module.css';
import { settingsApi, configApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import type { BasicConnector } from '../../../types/types';
import { FieldMeta, SettingsMap } from '../../../types/webchatTabsTypes';
import { FieldRenderProps } from './types';
import { matchesSearch } from '../tokenSettingsTab/tokenSettingsUtils';
import NumberCard from './NumberCard';
import ToggleRow from './ToggleRow';
import DataLakeLiveView from './DataLakeLiveView';
import SelectCard from './SelectCard';
import { SECTION_META, FIELDS_BY_SECTION, ALL_FIELDS, S3_FIELD_KEY } from './dataLakeFieldsConfig';

const S3_CONNECTION_TYPE_ID = 2;

type InnerTab = 'config' | 'live';
const SCROLL_OFFSET_PX = 16;

const DataLakeSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<SettingsMap>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [initialDraft, setInitialDraft] = useState<Record<string, string>>({});
  const [s3Connections, setS3Connections] = useState<BasicConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTION_META[0].id);
  const [showKeys, setShowKeys] = useState(false);
  const [search, setSearch] = useState('');
  const [innerTab, setInnerTab] = useState<InnerTab>('config');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollLockRef = useRef(false);

  const loadAll = async () => {
    const [rawSettings, rawConnections] = await Promise.all([
      settingsApi.getDataLakeSettings(),
      configApi.getBasic(),
    ]);

    const normalized: SettingsMap = {};
    const initial: Record<string, string> = {};
    for (const { key, fallbackDefault } of ALL_FIELDS) {
      const entry = rawSettings && typeof rawSettings === 'object' ? (rawSettings as Record<string, any>)[key] : undefined;
      const hasEntry = entry && typeof entry === 'object' && 'value' in entry && entry.value !== null && entry.value !== undefined;
      const value = hasEntry ? entry.value : fallbackDefault;
      normalized[key] = { value, default: fallbackDefault };
      initial[key] = String(value);
    }
    setSettings(normalized);
    setDraft(initial);
    setInitialDraft(initial);

    const conns = Array.isArray(rawConnections) ? rawConnections : [];
    setS3Connections(conns.filter((c: BasicConnector) => c.connectionTypeId === S3_CONNECTION_TYPE_ID && c.isActive));
  };

  useEffect(() => {
    loadAll().catch(() => toast.error('Failed to load Data Lake settings')).finally(() => setLoading(false));
  }, []);

  const dirtyKeys = useMemo(
    () => ALL_FIELDS.filter(({ key }) => draft[key] !== (initialDraft[key] ?? '')).map(f => f.key),
    [draft, initialDraft],
  );
  const isDirty = dirtyKeys.length > 0;

  const matchedKeys = useMemo(
    () => (search.trim() ? ALL_FIELDS.filter(f => matchesSearch(f, search.trim())).map(f => f.key) : []),
    [search],
  );

  const scrollToSection = (id: string) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    setActiveSection(id);
    scrollLockRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => { scrollLockRef.current = false; }, 800);
  };

  useEffect(() => {
    if (!search.trim()) return;
    const firstMatch = SECTION_META.find(s => FIELDS_BY_SECTION[s.id].some(f => matchedKeys.includes(f.key)));
    if (firstMatch) scrollToSection(firstMatch.id);
  }, [matchedKeys]);

  // Scroll-spy: highlight the nav item of the section currently near the top of the viewport.
  useEffect(() => {
    if (loading || innerTab !== 'config') return;
    const observer = new IntersectionObserver(
      entries => {
        if (scrollLockRef.current) return;
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.getAttribute('data-section') ?? SECTION_META[0].id);
      },
      { rootMargin: '0px 0px -70% 0px' },
    );
    Object.values(sectionRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [loading, innerTab]);

  const handleChange = (key: string, value: string) => setDraft(d => ({ ...d, [key]: value }));
  const handleToggle = (key: string) => setDraft(d => ({ ...d, [key]: d[key] === 'true' ? 'false' : 'true' }));

  const handleSave = async () => {
    for (const { key, label, min, type } of ALL_FIELDS) {
      if (type !== 'number') continue;
      const num = Number(draft[key]?.trim());
      if (draft[key]?.trim() === '' || isNaN(num)) { toast.error(`${label}: please enter a valid number`); return; }
      if (min !== undefined && num < min) { toast.error(`${label}: must be >= ${min}`); return; }
    }
    const payload: Record<string, string | number | boolean> = {};
    for (const { key, type } of ALL_FIELDS) {
      const raw = draft[key]?.trim() ?? '';
      payload[key] = type === 'number' ? Number(raw) : type === 'boolean' ? raw === 'true' : raw;
    }
    setSaving(true);
    try {
      await settingsApi.saveDataLakeSettings(payload);
      await loadAll();
      toast.success('Data Lake settings saved');
    } catch {
      toast.error('Failed to save Data Lake settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = (fields: FieldMeta[], message: string) => {
    setDraft(d => ({ ...d, ...Object.fromEntries(fields.map(f => [f.key, String(f.fallbackDefault)])) }));
    toast.info(message);
  };

  if (loading) return <div className={styles.loading}>Loading…</div>;

  const renderProps = (field: FieldMeta): FieldRenderProps => ({
    field,
    draft,
    initialDraft,
    settings,
    showKeys,
    highlighted: matchedKeys.includes(field.key),
    saving,
    onNumberChange: handleChange,
    onToggle: handleToggle,
  });

  const renderField = (f: FieldMeta) => {
    if (f.key === S3_FIELD_KEY) {
      const selected = s3Connections.find(c => c.id === draft[S3_FIELD_KEY]);
      return (
        <SelectCard
          key={f.key}
          {...renderProps(f)}
          options={s3Connections.map(c => ({ value: c.id, label: c.connectionName }))}
          emptyLabel="- None (Excel export disabled) -"
          hint={draft[S3_FIELD_KEY] ? `selected: ${selected?.connectionName ?? draft[S3_FIELD_KEY]}` : 'not configured'}
        />
      );
    }
    return f.type === 'boolean' ? null : <NumberCard key={f.key} {...renderProps(f)} />;
  };

  const renderSection = (id: string) => {
    const fields = FIELDS_BY_SECTION[id] ?? [];
    const toggles = fields.filter(f => f.type === 'boolean');
    const others = fields.filter(f => f.type !== 'boolean');
    return (
      <>
        {toggles.length > 0 && (
          <div className={styles.group}>
            {toggles.map(f => <div key={f.key} style={{ marginBottom: '0.625rem' }}><ToggleRow {...renderProps(f)} /></div>)}
          </div>
        )}
        {others.length > 0 && (
          <div className={styles.group}>
            <div className={styles.cardGrid}>{others.map(renderField)}</div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h2>Data Lake</h2>
          <p>Configure the analytical engine used for large dataset queries and Excel exports.</p>
        </div>
        {innerTab === 'config' && <div className={styles.headerActions}>
          <button type="button" className={styles.showKeysBtn} onClick={() => setShowKeys(s => !s)}>
            {showKeys ? 'Hide setting keys' : 'Show setting keys'}
          </button>
          <div className={styles.searchWrap}>
            <Search size={14} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search settings"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>}
      </div>

      <div className={styles.innerTabs}>
        <button
          type="button"
          className={`${styles.innerTab} ${innerTab === 'config' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('config')}
        >
          Configuration
        </button>
        <button
          type="button"
          className={`${styles.innerTab} ${innerTab === 'live' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('live')}
        >
          Live View
        </button>
      </div>

      {innerTab === 'live' && <DataLakeLiveView />}

      {innerTab === 'config' && (
        <>
      <div className={styles.body}>
        <div className={styles.nav}>
          {SECTION_META.map(s => (
            <button
              key={s.id}
              type="button"
              className={`${styles.navItem} ${activeSection === s.id ? styles.navItemActive : ''}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.heading}
            </button>
          ))}
        </div>

        <div className={styles.content}>
          {SECTION_META.map(s => (
            <section
              key={s.id}
              data-section={s.id}
              ref={el => { sectionRefs.current[s.id] = el; }}
              style={{ scrollMarginTop: SCROLL_OFFSET_PX, marginBottom: '2.5rem' }}
            >
              <div className={styles.contentHeader}>
                <h3>{s.heading}</h3>
                <p>{s.subtitle}</p>
              </div>
              {renderSection(s.id)}
            </section>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <span className={styles.footerStatus}>{isDirty ? `${dirtyKeys.length} unsaved change${dirtyKeys.length === 1 ? '' : 's'}` : 'No unsaved changes'}</span>
        <div className={styles.footerActions}>
          <button type="button" className={styles.resetBtn} disabled={saving || !isDirty} onClick={() => setDraft({ ...initialDraft })}>Discard changes</button>
          <button type="button" className={styles.resetBtn} disabled={saving} onClick={() => resetToDefaults(FIELDS_BY_SECTION[activeSection] ?? [], 'Section reset to defaults — save to apply')}>Reset section</button>
          <button type="button" className={styles.resetBtn} disabled={saving} onClick={() => resetToDefaults(ALL_FIELDS, 'All settings reset to defaults — save to apply')}>Reset all to defaults</button>
          <button type="button" className={styles.saveBtn} disabled={!isDirty || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default DataLakeSettingsTab;
